import update from 'immutability-helper';
import {
  GAME_LIST_REQUEST,
  GAME_LIST_SUCCESS,
  GAME_ABLY_UPDATED,
  GAME_ABLY_CREATED,
  PLAYER_ENTERED_GAME_BY_DIRECT_LINK,
  ON_MODAL_OPEN,
  ON_MODAL_CLOSE,
  PLAYER_JOINED_GAME,
  PLAYER_CONFIRM_MEDIA,
  PLAYER_LEFT_GAME,
  ABLY_PLAYER_JOINED_GAME,
  ABLY_PLAYER_LEFT_GAME,
  ABLY_PLAYER_READY,
  ABLY_GAME_HAS_STARTED,
  ABLY_GAME_ROUND_STARTED,
  ABLY_PLAYER_ANSWER_HAS_SELECTED,
  ABLY_GAME_QUESTION_NEXT,
  ABLY_PLAYER_ANSWER_HAS_CHECKED,
  ABLY_GAME_HAS_FINISHED,
  ABLY_GAME_INTERSTITIAL_STARTED,
  ABLY_GAME_INTERSTITIAL_FINISHED,
  ABLY_GAME_ADMIN_HAS_CHANGED,
  PLAYER_ANSWER_REQUEST,
  GAME_TEAM_DEFINITION_REQUEST,
  GAME_TEAM_DEFINITION_SUCCESS,
  GAME_MOBILE_BRANCHIO_LINK_REQUEST,
  GAME_MOBILE_BRANCHIO_LINK_SUCCESS,
  ABLY_PLAYER_GIFT_HAS_RECEIVED,
  PLAYER_NOTICE_CLEARED,
  TEAM_GETS_POINTS,
  PLAYER_GETS_POINTS,
  ABLY_USER_IS_ONLINE,
  ABLY_USER_IS_OFFLINE,
  ABLY_PLAYER_KICKED,
  ABLY_PLAYER_KICK_INIT,
  PLAYER_KICK_REQUEST,
  PLAYER_KICK_FAILURE,
  PLAYER_KICK_CANCEL,
  PLAYER_LEAVE_AS_KICKED,
  ABLY_GAME_RESPONDENT_HAS_BEEN_CHANGED,
  PLAYER_REMOTE_VIDEO_MUTED,
  PLAYER_REMOTE_VIDEO_UNMUTED,
  PLAYER_REJOIN_GAME,
  GAME_LANDING_SHOWED,
} from '../actions/types';
import {
  LIST_PAGE_SIZE,
  GAME_PHASE_PLAYING,
  GAME_PHASE_FINISHED,
  GAMEADMINTYPE,
} from '../configs/constants';
import {createReducer} from '../utils';

const initialState = {
  isModalOpened: false,
  list: [],
  hasMore: true,
  isGameListFetching: false,
  page: 1,
  query: '',
  isLandingShowed: false,
  currentTeamDefinition: {
    leftTeamName: '',
    rightTeamName: '',
    playerDefinition: [],
    teamDefinition: {
      preselected: null,
      field: null,
      values: [],
    },
  },
  fetchingCurrentTeamDefinition: false,
  currentGame: {
    name: '',
    players: [],
    audience: [],
    state: '',
    currentTeamName: '',
    round: {
      title: '',
    },
    interstitial: null,
    teams: {
      left: {
        name: 'Left',
        points: 0,
      },
      right: {
        name: 'Right',
        points: 0,
      },
      gifts: [],
    },
    isAnswerChecking: false,
    hasAlreadyJoined: false,
    playerKickError: '',
    kickRequest: null,
    youKicked: false,
    winner: null,
  },
};

const gameReducer = createReducer(initialState, {
  [ON_MODAL_OPEN]: state => update(state, {
    isModalOpened: {$set: true},
  }),

  [ON_MODAL_CLOSE]: state => update(state, {
    isModalOpened: {$set: false},
  }),

  [GAME_LIST_REQUEST]: (state, payload) => {
    const {query, page} = payload;
    return update(state, {
      isGameListFetching: {$set: true},
      query: {$set: query},
      page: {$set: page},
    });
  },

  [GAME_LIST_SUCCESS]: (state, payload) => {
    const {page} = state;
    const {response: {list: loadedList, count}} = payload;
    const hasMore = (LIST_PAGE_SIZE * page) < count;
    const setter = (page === 1) ? '$set' : '$push';

    return update(state, {
      list: {[setter]: loadedList},
      hasMore: {$set: hasMore},
      isGameListFetching: {$set: false},
      page: {$set: page + 1},
    });
  },

  [PLAYER_ENTERED_GAME_BY_DIRECT_LINK]: (state, payload) => {
    const {response: {
      isAudienceAllowed,
      name,
      hasAlreadyJoined,
      id,
      playerLimit,
      state: startedState,
      leftTeam,
      rightTeam,
    }} = payload;
    const gameIndex = state.list.findIndex(g => g.id === id);

    return update(state, {
      currentGame: {
        id: {$set: id},
        isAudienceAllowed: {$set: isAudienceAllowed},
        name: {$set: name},
        startedState: {$set: startedState},
        hasAlreadyJoined: {$set: hasAlreadyJoined},
        playerLimit: {$set: playerLimit},
        leftTeam: {$set: leftTeam},
        rightTeam: {$set: rightTeam},
      },
      list: {
        [gameIndex]: {$set: {...state.list[gameIndex], hasAlreadyJoined}},
      },
    });
  },

  [GAME_ABLY_CREATED]: (state, payload) => {
    const {newGame} = payload;

    return update(state, {
      list: {$unshift: [newGame]},
    });
  },

  [GAME_ABLY_UPDATED]: (state, payload) => {
    const {list, query} = state;
    const {updatedGame} = payload;
    const {name: gameName} = updatedGame;

    if (Boolean(query) && !gameName.match(query)) {
      return state;
    }

    const existedIdx = list.findIndex(game => game.id === updatedGame.id);
    const isFinishedGame = updatedGame.state === GAME_PHASE_FINISHED;

    if (isFinishedGame) {
      return update(state, {
        list: {$splice: [[existedIdx, 1]]},
      });
    }

    return update(state, {
      list: {$splice: [[existedIdx, 1, updatedGame]]},
    });
  },

  [PLAYER_JOINED_GAME]: (state, payload) => {
    const {response: currentGame, onlinePlayers} = payload;
    const {players = [], ...rest} = currentGame;
    const {name} = state.currentGame;
    const updatedPlayers = players.map((player) => {
      const isOnline = onlinePlayers.some(id => id === player.id);

      return {...player, isOnline};
    });

    return update(state, {
      currentGame: {$set: {...rest, name, players: updatedPlayers}},
    });
  },

  [PLAYER_CONFIRM_MEDIA]: (state, payload) => {
    const {audioSource, videoSource, userId} = payload;
    let userUpdate = {};
    if (userId) {
      const {currentGame: {players}} = state;
      const existedIdx = players.findIndex(item => item.id === userId);
      if (existedIdx !== -1) {
        const updatedPlayer = {...players[existedIdx], streamId: null};
        userUpdate = {
          currentGame: {players: {$splice: [[existedIdx, 1, updatedPlayer]]}},
        };
      }
    }

    return update(state, {
      ...userUpdate,
      audioSource: {$set: audioSource},
      videoSource: {$set: videoSource},
    });
  },

  [PLAYER_REJOIN_GAME]: (state, payload) => {
    const {data: {userId, streamId}} = payload;

    if (!userId) {
      return state;
    }

    const {currentGame: {players}} = state;
    const existedIdx = players.findIndex(item => item.id === userId);
    if (existedIdx === -1) {
      return state;
    }

    const updatedPlayer = {...players[existedIdx], streamId};

    return update(state, {
      currentGame: {players: {$splice: [[existedIdx, 1, updatedPlayer]]}},
    });
  },

  [ABLY_PLAYER_READY]: (state, payload) => {
    const {player} = payload;

    if (!player) {
      return state;
    }

    const {id: playerId} = player;
    const {currentGame: {players}} = state;
    const existedIdx = players.findIndex(item => item.id === playerId);

    if (existedIdx === -1) {
      return state;
    }
    const updatedPlayer = {...players[existedIdx], isReady: true};

    return update(state, {
      currentGame: {players: {$splice: [[existedIdx, 1, updatedPlayer]]}},
    });
  },

  [ABLY_PLAYER_JOINED_GAME]: (state, payload) => {
    const {newPlayer} = payload;
    const {currentGame: {players}} = state;
    const isCurrentPlayer = Boolean(players.find(player => player && player.id === newPlayer.id));
    const isAudience = newPlayer && newPlayer.type === 'audience';

    if (isCurrentPlayer) {
      return state;
    }

    if (isAudience) {
      return update(state, {
        currentGame: {audience: {$push: [newPlayer]}},
      });
    }

    newPlayer.isOnline = true;
    return update(state, {
      currentGame: {players: {$push: [newPlayer]}},
    });
  },

  [ABLY_GAME_ADMIN_HAS_CHANGED]: (state, payload) => {
    const {data: {adminId}} = payload;
    const {currentGame: {players}} = state;
    const existedIdx = players.findIndex(player => player.id === adminId);
    const newAdmin = {...players[existedIdx], type: GAMEADMINTYPE};

    return update(state, {
      currentGame: {players: {$splice: [[existedIdx, 1, newAdmin]]}},
    });
  },

  [PLAYER_LEFT_GAME]: (state, payload) => {
    const {response: {userId}} = payload;
    const {currentGame: {players}} = state;
    const existedIdx = players.findIndex(pl => pl.id === userId);

    if (existedIdx === -1) {
      return state;
    }

    return update(state, {
      currentGame: {players: {$splice: [[existedIdx, 1]]}},
    });
  },

  [ABLY_PLAYER_LEFT_GAME]: (state, payload) => {
    const {player} = payload;
    const {currentGame: {players, audience}} = state;
    const isAudience = player && player.type === 'audience';
    const existedIdx = players.findIndex(pl => pl.id === player.playerId);

    if (isAudience) {
      const audienceId = audience.findIndex(pl => pl.id === player.playerId);
      return update(state, {
        currentGame: {audience: {$splice: [[audienceId, 1]]}},
      });
    }

    if (existedIdx === -1) {
      return state;
    }

    return update(state, {
      currentGame: {players: {$splice: [[existedIdx, 1]]}},
    });
  },

  [ABLY_GAME_HAS_STARTED]: state => update(state, {
    currentGame: {state: {$set: GAME_PHASE_PLAYING}},
  }),

  [ABLY_USER_IS_ONLINE]: (state, payload) => {
    const {data: user} = payload;
    const {id: playerId} = user;
    const {currentGame: {players = []}} = state;
    const currentPlayerIdx = players.findIndex(player => player.id === playerId);
    if (currentPlayerIdx === -1) {
      return state;
    }
    const updatedPlayer = {...players[currentPlayerIdx], ...user, isOnline: true};

    return update(state, {
      currentGame: {players: {$splice: [[currentPlayerIdx, 1, updatedPlayer]]}},
    });
  },

  [ABLY_USER_IS_OFFLINE]: (state, payload) => {
    const {data: {id: playerId}} = payload;
    const {currentGame: {players = []}} = state;
    const currentPlayerIdx = players.findIndex(player => player.id === playerId);
    if (currentPlayerIdx === -1) {
      return state;
    }
    const updatedPlayer = {...players[currentPlayerIdx], isOnline: false};

    return update(state, {
      currentGame: {players: {$splice: [[currentPlayerIdx, 1, updatedPlayer]]}},
    });
  },

  [ABLY_GAME_QUESTION_NEXT]: (state, {data: {question, questionIndex, responder}}) =>
    update(state, {
      currentGame: {
        question: {$set: question},
        questionIndex: {$set: questionIndex},
        responder: {$set: responder},
        isAnswerChecking: {$set: false},
      },
    }),

  [ABLY_PLAYER_ANSWER_HAS_SELECTED]: (state, {answer: {answerId}}) => {
    const {
      currentGame: {question: {answers}},
    } = state;
    const updatedAnswers = answers.map(answer => (
      {...answer, isChosen: answer.id === answerId}
    ));

    return update(state, {
      currentGame: {question: {answers: {$set: updatedAnswers}}},
    });
  },

  [ABLY_PLAYER_ANSWER_HAS_CHECKED]: (state, {answer: {correctAnswerId}}) => {
    const {
      currentGame: {question: {answers}},
    } = state;

    const updatedAnswers = answers.map(answer => (
      {
        ...answer,
        isCorrect: answer.id === correctAnswerId,
      }
    ));

    return update(state, {
      currentGame: {
        question: {answers: {$set: updatedAnswers}},
      },
    });
  },

  [ABLY_GAME_HAS_FINISHED]: (state, {data: {winner}}) => update(state, {
    currentGame: {
      state: {$set: GAME_PHASE_FINISHED},
      winner: {$set: winner},
    },
  }),

  [ABLY_GAME_ROUND_STARTED]: (state, payload) => {
    const {round} = payload;
    return update(state, {
      currentGame: {round: {$set: round}},
    });
  },

  [ABLY_GAME_INTERSTITIAL_STARTED]: (state, payload) => {
    const {asset} = payload;
    if (!asset) {
      return state;
    }

    return update(state, {
      currentGame: {
        interstitial: {$set: asset},
        question: {$set: null},
      },
    });
  },

  [ABLY_GAME_INTERSTITIAL_FINISHED]: state => (
    update(state, {
      currentGame: {interstitial: {$set: null}},
    })
  ),

  [PLAYER_ANSWER_REQUEST]: state => update(state, {
    currentGame: {isAnswerChecking: {$set: true}},
  }),

  [GAME_TEAM_DEFINITION_REQUEST]: state => (
    update(state, {
      fetchingCurrentTeamDefinition: {$set: true},
    })
  ),

  [GAME_TEAM_DEFINITION_SUCCESS]: (state, payload) => {
    const {fields} = payload;
    return (
      update(state, {
        currentTeamDefinition: {$set: fields},
        fetchingCurrentTeamDefinition: {$set: false},
      })
    );
  },

  [GAME_MOBILE_BRANCHIO_LINK_REQUEST]: state => (
    update(state, {
      fetchingBranchIoLink: {$set: true},
    })
  ),

  [GAME_MOBILE_BRANCHIO_LINK_SUCCESS]: (state, payload) => {
    const {url} = payload;
    return (
      update(state, {
        branchIoLink: {$set: url},
        fetchingBranchIoLink: {$set: false},
      })
    );
  },

  [ABLY_PLAYER_GIFT_HAS_RECEIVED]: (state, payload) => {
    const {gift: {receiverId, name, emoji, senderId}} = payload;
    const {currentGame: {players, audience = []}} = state;
    const currentPlayer = players.find(player => player && player.id === receiverId);

    if (!currentPlayer) {
      return state;
    }

    const currentPlayerIdx = players.findIndex(player => player.id === currentPlayer.id);
    const sender = [...players, ...audience].find(player => player.id === senderId);
    currentPlayer.gifts = [...(currentPlayer.gifts || []), {name, emoji}];
    currentPlayer.notes = {
      type: 'emoji',
      emoji,
      senderName: sender && sender.firstName,
    };
    return update(state, {
      currentGame: {players: {$splice: [[currentPlayerIdx, 1, currentPlayer]]}},
    });
  },

  [PLAYER_KICK_REQUEST]: state => update(state, {
    currentGame: {
      playerKickError: {$set: ''},
    },
  }),

  [PLAYER_KICK_FAILURE]: (state, payload) => {
    const {error: {message}} = payload;
    return update(state, {
      currentGame: {
        playerKickError: {$set: message},
        kickRequest: {$set: null},
      },
    });
  },

  [ABLY_PLAYER_KICK_INIT]: (state, payload) => {
    const {data: {kickerId, playerIdToKick}, userId} = payload;
    const {currentGame: {players, audience = []}} = state;
    const kickerIdx = players.findIndex(player => player && player.id === kickerId);
    const toKickIdx = players.findIndex(player => player && player.id === playerIdToKick);
    const isAudience = audience.some(player => player && player.id === userId);

    if (
      kickerIdx === -1 || toKickIdx === -1 ||
      userId === kickerId || userId === playerIdToKick || isAudience
    ) {
      return state;
    }

    const kickRequest = {
      kicker: {...players[kickerIdx]},
      toKick: {...players[toKickIdx]},
    };

    return update(state, {
      currentGame: {
        kickRequest: {$set: kickRequest},
      },
    });
  },

  [PLAYER_KICK_CANCEL]: state => update(state, {
    currentGame: {
      kickRequest: {$set: null},
    },
  }),

  [ABLY_PLAYER_KICKED]: (state, payload) => {
    const {data: {playerIdToKick}, userId} = payload;
    const {currentGame: {players, youKicked}} = state;
    const toKickIdx = players.findIndex(player => player && player.id === playerIdToKick);
    const setYouKicked = youKicked || (userId === playerIdToKick);

    if (toKickIdx === -1) {
      return state;
    }

    const toKick = {...players[toKickIdx], notes: {type: 'kick'}, hasKicked: true};

    return update(state, {
      currentGame: {
        youKicked: {$set: setYouKicked},
        players: {$splice: [[toKickIdx, 1, toKick]]},
      },
    });
  },

  [PLAYER_LEAVE_AS_KICKED]: state => update(state, {
    currentGame: {
      youKicked: {$set: false},
    },
  }),

  [ABLY_GAME_RESPONDENT_HAS_BEEN_CHANGED]: (state, payload) => {
    const {data: responder} = payload;
    const {currentGame: {players}} = state;
    const currentPlayerIdx = players.findIndex(pl => pl && pl.id === responder.id);

    if (currentPlayerIdx === -1) {
      return state;
    }

    return update(state, {
      currentGame: {responder: {$set: responder}},
    });
  },

  [PLAYER_NOTICE_CLEARED]: (state, payload) => {
    const {playerId} = payload;
    const {currentGame: {players}} = state;
    const currentPlayer = players.find(player => player && player.id === playerId);

    if (!currentPlayer) {
      return state;
    }
    const currentPlayerIdx = players.findIndex(player => player.id === currentPlayer.id);
    currentPlayer.notes = {};
    return update(state, {
      currentGame: {players: {$splice: [[currentPlayerIdx, 1, currentPlayer]]}},
    });
  },

  [TEAM_GETS_POINTS]: (state, payload) => {
    const {data: {team, totalPoints}} = payload;
    const {currentGame: {teams}} = state;

    if (!teams[team]) {
      return state;
    }

    return update(state, {
      currentGame: {
        teams: {[team]: {points: {$set: totalPoints}}},
      },
    });
  },

  [PLAYER_GETS_POINTS]: (state, payload) => {
    const {data: {points}} = payload;
    const {currentGame: {players, responder: {id: playerId}}} = state;
    const currentPlayerIdx = players.findIndex(player => player.id === playerId);

    if (currentPlayerIdx === -1) {
      return state;
    }

    const updatedPlayer = {...players[currentPlayerIdx], points};

    return update(state, {
      currentGame: {players: {$splice: [[currentPlayerIdx, 1, updatedPlayer]]}},
    });
  },

  [PLAYER_REMOTE_VIDEO_MUTED]: (state, payload) => {
    const {data: {uid: streamId}} = payload;
    const {currentGame: {players}} = state;
    const currentPlayerIdx = players.findIndex(player => player.streamId === streamId);

    if (currentPlayerIdx === -1) {
      return state;
    }

    const updatedPlayer = {...players[currentPlayerIdx], isVideoRemotelyMuted: true};

    return update(state, {
      currentGame: {players: {$splice: [[currentPlayerIdx, 1, updatedPlayer]]}},
    });
  },

  [PLAYER_REMOTE_VIDEO_UNMUTED]: (state, payload) => {
    const {data: {uid: streamId}} = payload;
    const {currentGame: {players}} = state;
    const currentPlayerIdx = players.findIndex(player => player.streamId === streamId);

    if (currentPlayerIdx === -1) {
      return state;
    }

    const updatedPlayer = {...players[currentPlayerIdx], isVideoRemotelyMuted: false};

    return update(state, {
      currentGame: {players: {$splice: [[currentPlayerIdx, 1, updatedPlayer]]}},
    });
  },

  [GAME_LANDING_SHOWED]: state => update(state, {
    isLandingShowed: {$set: true},
    currentGame: {
      hasAlreadyJoined: {$set: true},
    },
  }),
});

export default gameReducer;
